import type { Ref } from 'vue';
import { computed } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { storeToRefs } from 'pinia';
import { Feature, useFeatureStore } from '@getaccept/feature-restriction';
import { nanoid } from 'nanoid';
import type { PricingTableColumn, PricingTableSection } from '../types/pricing-table';
import {
  isProductPropertyColumn,
  isColumnEnabled,
  isGridColumn,
  isPredefinedColumn,
} from '../helpers/pricing-table/pricing-table-column.helper';
import { EPricingTableColumn } from '../types/enums';
import {
  customColumnNamePrefix,
  maxCustomColumns,
  minColumnWidth,
} from '../constants/pricing-table';
import { useEditorMetaDataStore } from '../store/editor-meta-data.store';
import type { EditorProductProperty } from '../types/editor-product-property';
import * as EditorHelper from '../helpers/editor.helper';
import * as PricingTableHelper from '../helpers/pricing-table/pricing-table.helper';

export function usePricingTableColumns(
  section: Ref<PricingTableSection>,
  preCalculated: Ref<boolean>,
  productProperties: Ref<EditorProductProperty[]>
) {
  const { features } = storeToRefs(useFeatureStore());

  const metaDataStore = useEditorMetaDataStore();

  const columns = computed(() => section.value?.columns || []);
  const enabledColumns = computed(() => PricingTableHelper.getEnabledColumns(columns.value));
  const columnWidths = computed(() => enabledColumns.value?.map(({ width }) => width));

  const customColumns = computed(() => columns.value.filter(({ isCustom }) => isCustom));
  const customColumnThresholdReached = computed(() => {
    const maxColumns = !features.value[Feature.CustomColumns].restricted ? maxCustomColumns : 1;
    return customColumns.value.length >= maxColumns;
  });

  const maxEnabledColumnsCount = Math.floor(100 / minColumnWidth);

  const vacantColumnCount = computed(() =>
    Math.max(0, maxEnabledColumnsCount - enabledColumns.value.length)
  );

  const maxEnabledColumnsReached = computed(
    () => enabledColumns.value.length >= maxEnabledColumnsCount
  );

  const isFirstColumn = (columnId: string) => enabledColumns.value[0].id === columnId;
  const isSecondColumn = (columnId: string) => enabledColumns.value[1].id === columnId;
  const isLastColumn = (columnId: string) =>
    enabledColumns.value[enabledColumns.value.length - 1].id === columnId;
  const isSecondToLastColumn = (columnId: string) =>
    enabledColumns.value[enabledColumns.value.length - 2].id === columnId;

  const getColumnById = (columnId: string) => columns.value.find(({ id }) => id === columnId);

  const getColumnByName = (columnName: EPricingTableColumn) =>
    columns.value.find(({ name }) => name === columnName);

  const hasImagesColumn = computed(() => !!getColumnByName(EPricingTableColumn.Images));

  const imageDisplayMode = computed(
    () => section.value?.displayMode?.imageDisplayMode || 'InlineWithName'
  );

  const imagesColumn = computed(() => getColumnByName(EPricingTableColumn.Images));

  const visibleColumns = computed(() =>
    PricingTableHelper.getVisibleColumns(columns.value, imageDisplayMode.value)
  );

  const visibleColumnWidths = computed(() => {
    const adjustedWidths = PricingTableHelper.getAdjustedColumnWidths(visibleColumns.value);
    return visibleColumns.value.map(column => adjustedWidths[column.id] || column.width);
  });

  const enableColumn = (column: PricingTableColumn, updatedSection?: PricingTableSection) => {
    if (isProductPropertyColumn(column)) {
      metaDataStore.productPropertyChangedEvent.trigger();
    }

    return preCalculated.value
      ? PricingTableHelper.enableColumn(column.id, updatedSection || section.value)
      : PricingTableHelper.enableColumnAndReCalculate(column.id, updatedSection || section.value);
  };

  const disableColumn = (column: PricingTableColumn) => {
    if (isProductPropertyColumn(column)) {
      metaDataStore.productPropertyChangedEvent.trigger();
    }

    let updatedSection = preCalculated.value
      ? PricingTableHelper.disableColumn(column.id, section.value)
      : PricingTableHelper.disableColumnAndReCalculate(column.id, section.value);

    if (column.name === EPricingTableColumn.TotalPrice) {
      const discountColumn = getColumnByName(EPricingTableColumn.Discount);
      updatedSection = preCalculated.value
        ? PricingTableHelper.disableColumn(discountColumn.id, updatedSection)
        : PricingTableHelper.disableColumnAndReCalculate(discountColumn.id, updatedSection);
    }

    return updatedSection;
  };

  const discountColumn = computed(() => getColumnByName(EPricingTableColumn.Discount));
  const totalPriceColumn = computed(() => getColumnByName(EPricingTableColumn.TotalPrice));
  const taxColumn = computed(() => getColumnByName(EPricingTableColumn.Tax));

  const getColumnByProductPropertyId = (productPropertyId: string) =>
    columns.value.find(column => column.productPropertyId === productPropertyId);

  const getProductPropertyById = (productPropertyId: string) =>
    (productProperties.value || []).find(({ id }) => id === productPropertyId);

  const isProductPropertyEnabled = (productPropertyId: string) =>
    isColumnEnabled(getColumnByProductPropertyId(productPropertyId));

  const shouldReferenceProductProperty = (column: PricingTableColumn) =>
    isProductPropertyColumn(column) && metaDataStore.isProductPropertyReferenceContext;

  const createColumnFromProductProperty = (
    productPropertyId: string,
    columnProps?: Partial<PricingTableColumn>
  ) => {
    const productProperty = getProductPropertyById(productPropertyId);
    return metaDataStore.isProductPropertyReferenceContext
      ? PricingTableHelper.createColumn({
          productPropertyId,
          type: productProperty.type,
          unit: productProperty.type === 'Number' ? productProperty.unit : null,
          ...(columnProps ?? {}),
        })
      : PricingTableHelper.createColumnFromProductProperty(productProperty, columnProps);
  };

  const insertColumn = (
    newColumn: PricingTableColumn,
    section: PricingTableSection,
    referenceColumnId?: string,
    offset: number = 1
  ): PricingTableSection => {
    let referenceColumnIndex = referenceColumnId
      ? section.columns.findIndex(({ id }) => id === referenceColumnId)
      : -1;

    if (referenceColumnIndex === -1) {
      referenceColumnIndex = section.columns.length - 2;
    }

    const updatedColumns = [...section.columns];
    updatedColumns.splice(referenceColumnIndex + offset, 0, newColumn);

    return {
      ...section,
      columns: updatedColumns,
    };
  };

  const enableProductProperties = (productPropertyIds: string[]) => {
    let updatedSection = { ...section.value };

    productPropertyIds.forEach(productPropertyId => {
      const existingColumn = getColumnByProductPropertyId(productPropertyId);

      if (existingColumn) {
        updatedSection = enableColumn(existingColumn, updatedSection);
        return;
      }

      const column = createColumnFromProductProperty(productPropertyId);
      updatedSection = insertColumn(column, updatedSection);
      updatedSection = enableColumn(column, updatedSection);
    });

    return updatedSection;
  };

  const disableProductProperties = (productPropertyIds: string[]) => {
    let updatedSection = { ...section.value };

    productPropertyIds.forEach(productPropertyId => {
      updatedSection = disableColumn(getColumnByProductPropertyId(productPropertyId));
    });

    return updatedSection;
  };

  const getColumnDisplayName = (column?: PricingTableColumn) => {
    if (!column) return '';
    if (shouldReferenceProductProperty(column)) {
      return (
        column.displayName || getProductPropertyById(column.productPropertyId)?.displayName || ''
      );
    }

    if (column.displayName) {
      return column.displayName;
    }

    if (isPredefinedColumn(column)) {
      switch (column.name) {
        case EPricingTableColumn.TotalPrice:
          return t('cpq_amount');
        case EPricingTableColumn.Images:
          return t('image');
        default:
          return column.name ? t(column.name) : '';
      }
    }

    return '';
  };

  const getColumnName = (column: PricingTableColumn) => {
    if (shouldReferenceProductProperty(column)) {
      return getProductPropertyById(column.productPropertyId)?.name || '';
    }

    return column.name;
  };

  const hasProductPropertyReferences = computed(() =>
    columns.value.some(
      column => metaDataStore.isProductPropertyReferenceContext && isProductPropertyColumn(column)
    )
  );

  const moveColumn = (
    section: PricingTableSection,
    targetColumnId: string,
    referenceColumnId: string,
    offset: number
  ) => {
    const targetColumnIndex = section.columns.findIndex(({ id }) => id === targetColumnId);
    const referenceColumnIndex = section.columns.findIndex(({ id }) => id === referenceColumnId);

    if (targetColumnIndex === -1 || referenceColumnIndex === -1) {
      return section;
    }

    const updatedColumns = [...section.columns];
    const [targetColumn] = updatedColumns.splice(targetColumnIndex, 1);
    updatedColumns.splice(referenceColumnIndex + offset, 0, targetColumn);

    return {
      ...section,
      columns: updatedColumns,
    };
  };

  const nudgeColumn = (columnId: string, direction: 'left' | 'right') => {
    const isEnabledGridColumn = (column: PricingTableColumn) =>
      isGridColumn(column) && isColumnEnabled(column);

    const columnIndex = columns.value.findIndex(({ id }) => id === columnId);

    if (columnIndex === -1) {
      return;
    }

    const offset = direction === 'left' ? -1 : 1;
    let referenceColumnIndex = columnIndex + offset;

    while (
      referenceColumnIndex >= 0 &&
      referenceColumnIndex < columns.value.length &&
      !isEnabledGridColumn(columns.value[referenceColumnIndex])
    ) {
      referenceColumnIndex += offset;
    }

    if (referenceColumnIndex < 0 || referenceColumnIndex >= columns.value.length) {
      return;
    }

    return moveColumn(section.value, columnId, columns.value[referenceColumnIndex].id, 0);
  };

  const createCustomColumn = () => {
    const name = `${customColumnNamePrefix}${customColumns.value.length + 1}`;

    const newCustomColumn: PricingTableColumn = {
      id: nanoid(EditorHelper.NANO_ID_LENGTH),
      name,
      displayName: t('cpq_custom_column'),
      enabled: true,
      width: minColumnWidth,
      isCustom: true,
    };

    return newCustomColumn;
  };
  return {
    columns,
    enabledColumns,
    columnWidths,
    visibleColumns,
    visibleColumnWidths,
    customColumns,
    discountColumn,
    totalPriceColumn,
    taxColumn,
    customColumnThresholdReached,
    vacantColumnCount,
    maxEnabledColumnsReached,
    getColumnDisplayName,
    getColumnName,
    getColumnByName,
    getColumnById,
    enableColumn,
    disableColumn,
    hasImagesColumn,
    isProductPropertyEnabled,
    enableProductProperties,
    disableProductProperties,
    getColumnByProductPropertyId,
    createColumnFromProductProperty,
    getProductPropertyById,
    hasProductPropertyReferences,
    moveColumn,
    nudgeColumn,
    isFirstColumn,
    isSecondColumn,
    isLastColumn,
    isSecondToLastColumn,
    createCustomColumn,
    insertColumn,
    imagesColumn,
    imageDisplayMode,
  };
}
